<script lang="js">
export default {
  data() {
    return {
      lastUpdated: "August 2, 2026",
      sections: [
        {
          number: 1,
          title: "Information We Collect",
          subsections: [
            {
              subtitle: "1.1 Information Provided Directly",
              content:
                "We collect information you provide directly to us, such as when you create an account, apply for admission, or contact us. This may include your name, email address, phone number, educational background, residential address, date of birth, and other personal information necessary for processing your application or providing educational services.",
            },
            {
              subtitle: "1.2 Information Collected Automatically",
              content:
                "When you visit our website, we automatically collect certain information about your device and browsing activities, including IP address, browser type, operating system, referring URLs, pages visited, and the time and duration of your visit. We use cookies and similar tracking technologies to enhance your experience and analyze usage patterns.",
            },
            {
              subtitle: "1.3 Information from Third Parties",
              content:
                "We may receive information about you from educational institutions, government agencies, payment processors, and other third parties as needed to verify credentials or process applications. We will only use such information in accordance with this Privacy Policy.",
            },
          ],
        },
        {
          number: 2,
          title: "How We Use Your Information",
          subsections: [
            {
              subtitle: "2.1 Primary Uses",
              content:
                "We use your information to process and review your application, provide educational services, facilitate course registration, issue transcripts, process payments, and communicate with you about your admission status, academic progress, and institutional updates.",
            },
            {
              subtitle: "2.2 Communication and Notifications",
              content:
                "We may use your email and phone number to send you important notices, updates about programs, information about upcoming events, and other announcements relevant to your academic journey at First City Polytechnic.",
            },
            {
              subtitle: "2.3 Service Improvement",
              content:
                "We analyze how our website and services are used to identify trends, improve functionality, and enhance the overall user experience. This analysis helps us develop better features and deliver more relevant content to our students and prospective applicants.",
            },
          ],
        },
        {
          number: 3,
          title: "Data Security and Protection",
          subsections: [
            {
              subtitle: "3.1 Security Measures",
              content:
                "We implement industry-standard technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Our systems use encryption, secure sockets layer (SSL) technology, and regular security audits.",
            },
            {
              subtitle: "3.2 Limitations of Security",
              content:
                "While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data, and you use our services at your own risk.",
            },
            {
              subtitle: "3.3 Data Retention",
              content:
                "We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless longer retention is required by law. When information is no longer needed, we securely delete or anonymize it.",
            },
          ],
        },
        {
          number: 4,
          title: "Data Sharing and Disclosure",
          subsections: [
            {
              subtitle: "4.1 Service Providers",
              content:
                "We may share your information with trusted service providers who assist us in operating our website, conducting our business, and providing educational services. These include payment processors, email providers, hosting services, and educational analytics platforms. All service providers are contractually bound to maintain confidentiality.",
            },
            {
              subtitle: "4.2 Legal Compliance",
              content:
                "We may disclose your information when required by law, court order, or government authority. We will only disclose the minimum necessary information and will attempt to notify you of such disclosures unless legally prohibited.",
            },
            {
              subtitle: "4.3 No Sale of Data",
              content:
                "We do not sell, trade, or rent your personal information to third parties for their marketing purposes. Your information is used exclusively for the purposes stated in this Privacy Policy.",
            },
          ],
        },
        {
          number: 5,
          title: "Cookies and Tracking Technologies",
          subsections: [
            {
              subtitle: "5.1 Cookie Usage",
              content:
                "Our website uses cookies, web beacons, and similar tracking technologies to enhance your browsing experience, remember your preferences, and analyze website usage patterns. These help us understand how visitors use our site and improve our services.",
            },
            {
              subtitle: "5.2 Cookie Control",
              content:
                "You can control cookie settings through your browser preferences. Most browsers allow you to refuse cookies or alert you when cookies are being sent. However, disabling cookies may limit your ability to use certain features of our website.",
            },
            {
              subtitle: "5.3 Third-Party Analytics",
              content:
                "We may use third-party analytics services to track and analyze website usage. These services may collect information about your browsing activities across our site and other websites. Please refer to their privacy policies for more information.",
            },
            {
              subtitle: "5.4 Admission Partner Referrals",
              content:
                "When you follow an Admission Partner referral link, we store a first-party referral cookie for up to 30 days. It helps us attribute a completed application registration to the most recently selected partner and measure aggregated admission progress. The cookie contains a random identifier rather than your application number or personal details. Referral attribution becomes fixed after successful registration and does not change your admission decision, fees, or eligibility.",
            },
          ],
        },
        {
          number: 6,
          title: "Your Privacy Rights",
          subsections: [
            {
              subtitle: "6.1 Right to Access",
              content:
                "You have the right to request access to your personal information that we hold. We will provide you with a copy of your data in a structured, commonly used format within 30 days of your request.",
            },
            {
              subtitle: "6.2 Right to Update and Correction",
              content:
                "You may request that we update, correct, or amend any inaccurate or incomplete personal information in our records. We will make reasonable efforts to correct information promptly.",
            },
            {
              subtitle: "6.3 Right to Deletion",
              content:
                "You may request deletion of your personal information, subject to legal and contractual obligations. We will comply with such requests unless we are required to retain the information by law.",
            },
            {
              subtitle: "6.4 Right to Withdraw Consent",
              content:
                "If you have provided consent for certain uses of your information, you may withdraw that consent at any time. We will cease processing your information for those purposes, though previously processed data may not be retrievable.",
            },
          ],
        },
        {
          number: 7,
          title: "Contact Us",
          subsections: [
            {
              subtitle: "7.1 Privacy Inquiries",
              content:
                "If you have questions, concerns, or would like to exercise any of your privacy rights, please contact our Privacy Officer at privacy@firstcitypoly.edu.ng. You may also reach us by phone at +234 (0) 706 337 7322 or visit our office at 18, Quarry Road, Ibara, Abeokuta, Ogun State, Nigeria.",
            },
            {
              subtitle: "7.2 Response Time",
              content:
                "We are committed to responding to all privacy-related requests and inquiries within 30 days. If we require additional information to process your request, we will notify you accordingly.",
            },
          ],
        },
      ],
    };
  },
};
</script>

<template>
  <div class="privacy-policy">
    <!-- Hero Section -->
    <div class="hero-section">
      <div class="hero-overlay"></div>
      <div class="container hero-content">
        <h1>Privacy Policy</h1>
        <p class="published-date">
          Last updated and effective on {{ lastUpdated }}
        </p>
      </div>
    </div>

    <!-- Introduction Section -->
    <div class="container my-5">
      <div class="intro-section">
        <p class="intro-text">
          At First City Polytechnic, we are committed to protecting your privacy
          and ensuring you have a transparent understanding of how we collect,
          use, and safeguard your personal information. This Privacy Policy
          explains our practices regarding data collection and usage. By
          accessing and using our website and services, you agree to the terms
          outlined in this policy. Please read it carefully and contact us if
          you have any questions or concerns about our privacy practices.
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="container content-section">
      <div class="row">
        <div class="col-lg-10 offset-lg-1">
          <div v-for="(section, idx) in sections" :key="idx" class="section">
            <h2 class="section-title">
              <span class="section-number">{{ section.number }}</span>
              {{ section.title }}
            </h2>

            <div
              v-for="(subsection, subIdx) in section.subsections"
              :key="subIdx"
              class="subsection"
            >
              <h3 class="subsection-title">{{ subsection.subtitle }}</h3>
              <p class="subsection-content">{{ subsection.content }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Note -->
    <div class="container pb-5">
      <div class="row">
        <div class="col-lg-10 offset-lg-1">
          <div class="footer-note">
            <h5>Updates to This Policy</h5>
            <p>
              First City Polytechnic reserves the right to update this Privacy
              Policy at any time to reflect changes in our practices,
              technology, legal requirements, or other factors. We will notify
              you of material changes by updating the "Last updated" date at the
              top of this policy. Your continued use of our services after such
              modifications constitutes your acceptance of the updated Privacy
              Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.privacy-policy {
  background-color: #f8f9fa;
}

/* Hero Section */
.hero-section {
  position: relative;
  background: linear-gradient(135deg, #0e1438 0%, #1a2456 50%, #2d3a5f 100%);
  color: white;
  padding: 100px 0;
  text-align: center;
  overflow: hidden;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  z-index: 0;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-section h1 {
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 15px;
  letter-spacing: -0.5px;
}

.published-date {
  font-size: 1.1rem;
  color: #ffc107;
  font-weight: 500;
  margin: 0;
}

/* Introduction Section */
.intro-section {
  background-color: white;
  padding: 40px;
  border-radius: 8px;
  border-left: 5px solid #ffc107;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.intro-text {
  color: #333;
  font-size: 1.05rem;
  line-height: 1.8;
  margin: 0;
}

/* Content Section */
.content-section {
  padding: 60px 0;
}

.section {
  margin-bottom: 60px;
  background-color: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.8rem;
  font-weight: 700;
  color: #0e1438;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}

.section-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background-color: #ffc107;
  color: #0e1438;
  border-radius: 50%;
  font-weight: 700;
  font-size: 1.3rem;
  margin-right: 20px;
  flex-shrink: 0;
}

.subsection {
  margin-bottom: 25px;
}

.subsection:last-child {
  margin-bottom: 0;
}

.subsection-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #0e1438;
  margin-bottom: 12px;
}

.subsection-content {
  color: #555;
  line-height: 1.8;
  margin: 0;
}

/* Footer Note */
.footer-note {
  background-color: #e8f0fe;
  border-left: 5px solid #0e1438;
  padding: 30px;
  border-radius: 8px;
}

.footer-note h5 {
  color: #0e1438;
  font-weight: 700;
  margin-bottom: 15px;
  font-size: 1.2rem;
}

.footer-note p {
  color: #555;
  line-height: 1.8;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .hero-section h1 {
    font-size: 2.5rem;
  }

  .section {
    padding: 25px;
  }

  .section-title {
    font-size: 1.4rem;
  }

  .section-number {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
    margin-right: 15px;
  }

  .intro-section {
    padding: 25px;
  }
}
</style>
